import { Plural, Trans, useLingui } from "@lingui/macro";
import { Mail, ShieldCheck } from "lucide-react";
import { Button } from "@app/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@app/components/ui/dialog";
import type { ExportAllowance } from "@app/utils/use-export-share-cap";

/** Before an export outside the patientèle: what it takes, and the contact when the cap refuses it. */
export function ExportShareCapDialog({
  allowance,
  onClose,
}: {
  allowance: ExportAllowance | null;
  onClose: (go: boolean) => void;
}) {
  const { i18n } = useLingui();
  const percent = new Intl.NumberFormat(i18n.locale || "fr", {
    style: "percent",
    maximumFractionDigits: 1,
  });
  const share = allowance ? percent.format(allowance.share) : "";
  const cap = allowance ? percent.format(allowance.cap) : "";
  const contact = allowance?.contact ?? "";
  const already =
    allowance?.outside != null && allowance.new != null
      ? allowance.outside - allowance.new
      : 0;
  const byOrganisation = allowance?.holder === "organisation";

  return (
    <Dialog
      open={allowance !== null}
      onOpenChange={(open) => !open && onClose(false)}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-900">
            <ShieldCheck className="h-5 w-5 text-primary" />
            {allowance?.allowed ? (
              <Trans>Export hors de votre patientèle</Trans>
            ) : (
              <Trans>Export à valider avec l'équipe MAGE-X</Trans>
            )}
          </DialogTitle>
          <DialogDescription className="text-slate-600">
            {allowance &&
              (allowance.outside != null ? (
                <Plural
                  value={allowance.outside}
                  one="Cet export contient # patient hors de votre patientèle."
                  other="Cet export contient # patients hors de votre patientèle."
                />
              ) : (
                <Trans>
                  Cet export contient quelques patients hors de votre
                  patientèle.
                </Trans>
              ))}
          </DialogDescription>
        </DialogHeader>

        {allowance && (
          <div className="space-y-3 text-sm text-slate-700">
            {allowance.allowed ? (
              <p>
                {byOrganisation ? (
                  <Trans>
                    Avec cet export, votre organisation aura extrait {share} des
                    dossiers de la spécialité hors de votre patientèle (plafond
                    : {cap}).
                  </Trans>
                ) : (
                  <Trans>
                    Avec cet export, vous aurez extrait {share} des dossiers de
                    la spécialité hors de votre patientèle (plafond : {cap}).
                  </Trans>
                )}
              </p>
            ) : (
              <>
                <p>
                  {byOrganisation ? (
                    <Trans>
                      Cet export dépasse la part de la spécialité que votre
                      organisation peut extraire : il la porterait à {share},
                      au-delà du plafond de {cap}.
                    </Trans>
                  ) : (
                    <Trans>
                      Cet export dépasse la part de la spécialité que votre
                      compte peut extraire : il la porterait à {share}, au-delà
                      du plafond de {cap}.
                    </Trans>
                  )}
                </p>
                <p>
                  <Trans>
                    Les exports de cette taille se valident avec l'équipe
                    MAGE-X. Contactez-nous à{" "}
                    <a
                      href={`mailto:${contact}`}
                      className="font-medium text-primary underline-offset-2 hover:underline"
                    >
                      {contact}
                    </a>
                    .
                  </Trans>
                </p>
              </>
            )}
            {already > 0 && (
              <p className="text-slate-500">
                {byOrganisation ? (
                  <Plural
                    value={already}
                    one="# d'entre eux a déjà été exporté par votre organisation et ne compte pas à nouveau."
                    other="# d'entre eux ont déjà été exportés par votre organisation et ne comptent pas à nouveau."
                  />
                ) : (
                  <Plural
                    value={already}
                    one="# d'entre eux a déjà été exporté par votre compte et ne compte pas à nouveau."
                    other="# d'entre eux ont déjà été exportés par votre compte et ne comptent pas à nouveau."
                  />
                )}
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          {allowance?.allowed ? (
            <>
              <Button variant="outline" onClick={() => onClose(false)}>
                <Trans>Annuler</Trans>
              </Button>
              <Button onClick={() => onClose(true)}>
                <Trans>Exporter</Trans>
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => onClose(false)}>
                <Trans>Fermer</Trans>
              </Button>
              <Button asChild>
                <a href={`mailto:${contact}`} className="gap-2">
                  <Mail className="h-4 w-4" />
                  <Trans>Nous écrire</Trans>
                </a>
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
